import { saveRecoveryPackage } from "./recovery";
import { hex } from "@scure/base";
import type { BTC_NETWORK } from "@scure/btc-signer/utils.js";
import { NETWORK } from "@scure/btc-signer";

import { authorizeInscriptionCommit } from "./api";
import { resumeRecoveryPackage, type RecoveryStage } from "./recovery-api";
import {
  finalizeCommitPsbt,
  finalizeRevealPsbt,
  inscriptionOutputManifestForPlan,
  inscriptionRareSatManifestForPlan,
  mineRevealTransaction,
  type InscriptionPlan,
} from "./inscriptions";
import type { ConnectedWallet, SignedPackage } from "./types";
import { signPsbtGroups } from "./wallets";

export type InscriptionStage =
  | "signing-commit"
  | "building-reveal"
  | "mining-vanity"
  | "signing-reveal"
  | RecoveryStage
  | "complete";

export interface ExecuteInscriptionOptions {
  plan: InscriptionPlan;
  wallet: ConnectedWallet;
  feeAuthorization: string;
  sessionAuthorization?: string;
  skipBroadcast?: boolean;
  network?: BTC_NETWORK;
  onStage?: (stage: InscriptionStage) => void;
  onVanityProgress?: (attempt: number) => void;
}

export async function executeInscription({
  plan,
  wallet,
  feeAuthorization,
  sessionAuthorization,
  skipBroadcast = false,
  network = NETWORK,
  onStage,
  onVanityProgress,
}: ExecuteInscriptionOptions): Promise<SignedPackage> {
  onStage?.("signing-commit");
  const commitGroups: Record<string, number[]> = {
    [wallet.payment.address]: Array.from(
    { length: plan.selectedUtxos.length },
      (_, index) => index,
    ),
  };
  const signedCommitPsbt = await signPsbtGroups(wallet, plan.commit.toPSBT(0), commitGroups);
  const commit = finalizeCommitPsbt(signedCommitPsbt, plan.commit);

  const hasVanity = Boolean(plan.revealOptions.vanityStart || plan.revealOptions.vanityEnd);
  onStage?.(hasVanity ? "mining-vanity" : "building-reveal");
  const reveal = await mineRevealTransaction(plan, commit.id, network, onVanityProgress);
  const ordinalRevealIndexes = Array.from(
    { length: plan.revealInputIndex + (plan.ephemeralPrivateKey ? 0 : 1) },
    (_, index) => index,
  );
  let signedRevealPsbt = reveal.toPSBT(0);
  if (ordinalRevealIndexes.length) {
    onStage?.("signing-reveal");
    signedRevealPsbt = await signPsbtGroups(wallet, signedRevealPsbt, {
      [wallet.ordinals.address]: ordinalRevealIndexes,
    });
  }
  const finalizedReveal = finalizeRevealPsbt(signedRevealPsbt, plan, reveal);
  const commitAuthorization = await authorizeInscriptionCommit(
    commit.hex,
    feeAuthorization,
    finalizedReveal.hex,
    inscriptionOutputManifestForPlan(plan),
    inscriptionRareSatManifestForPlan(plan),
    sessionAuthorization,
  );

  const signedPackage: SignedPackage = {
    commitTxid: commit.id,
    revealTxid: finalizedReveal.id,
    commitHex: commit.hex,
    revealHex: finalizedReveal.hex,
    commitAuthorization: commitAuthorization.authorization,
    recovery: plan.ephemeralPrivateKey ? {
      ephemeralPrivateKey: hex.encode(plan.ephemeralPrivateKey),
      revealScript: hex.encode(plan.revealPayment.tapLeafScript?.[0]?.[1] ?? new Uint8Array()),
      revealInternalKey: hex.encode(plan.revealPayment.tapInternalKey ?? new Uint8Array()),
      tapMerkleRoot: hex.encode(plan.revealPayment.tapMerkleRoot ?? new Uint8Array()),
    } : undefined,
  };

  try { await saveRecoveryPackage(wallet.payment.address, signedPackage); }
  catch { downloadSignedPackage({ ...signedPackage, recovery: undefined }); }

  if (!skipBroadcast) {
    await resumeRecoveryPackage(signedPackage, onStage);
  }

  onStage?.("complete");
  return signedPackage;
}

export function downloadSignedPackage(signedPackage: SignedPackage): void {
  const data = JSON.stringify(signedPackage, null, 2);
  const url = URL.createObjectURL(new Blob([data], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `spam-the-chain-${signedPackage.revealTxid.slice(0, 12)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
